/**
 * Αντιστοίχιση προϊόντων πλατφόρμας (efood / BOX / Wolt) του ΚΑΤΑΣΤΗΜΑΤΟΣ — ανοίγει από τη σελίδα
 * «Πλατφόρμες» (PlatformIntegration) με τα ίδια props όπως πάντα: { isOpen, onClose, platform }.
 *
 * Νέα εμφάνιση (14/10/2026): συρτάρι δεξιά (shared/listPage DetailPanel, lg) αντί για το μεγάλο
 * σκούρο παράθυρο με λογότυπο. Οι καρτέλες «Ονόματα» / «Κωδικοί πλατφόρμας» είναι SubTabs, τα
 * μηνύματα Banner, οι καταστάσεις StatusChip, και μόνο tokens — ίδια όψη στο ανοιχτό «Χαρτί» και
 * στο σκούρο «Γραφίτης». Οι ενέργειες του παραθύρου (αυτόματη αντιστοίχιση, λήψη καταλόγου) κάτω
 * δεξιά, όπως σε κάθε συρτάρι. Ίδια endpoints, ίδια σώματα, ίδιοι κανόνες δικαιωμάτων, ίδια
 * προεπιλεγμένη καρτέλα και ίδιες ταξινομήσεις με πριν — άλλαξε μόνο η όψη και η δομή.
 */
import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Building2, Check, ChevronDown, Download, Lightbulb, Link2, Loader2, Ruler, Search, X,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';
import { mappingProductPrice, type MappingSize } from '../utils/adminPriceDisplay';
import { sizeChannelPrice } from '../utils/pricing';
import { formatMoney } from '../utils/formatMoney';
// Οι αντιστοιχίσεις ΟΝΟΜΑΤΩΝ περνούν από το axios instance (services/api μέσω
// platformMappingApi) — δεν ξαναγράφουμε fetch με χειροκίνητο Authorization
// header για κάθε νέα κλήση.
import {
  storeNameMappingsAPI,
  isMappingConflict,
  mappingErrorText,
  fixedComponentId,
  parseFixedComponents,
  rowTargetKind,
  MAX_COMPONENT_QUANTITY,
  MAX_FIXED_COMPONENTS,
  MappingFixedComponent,
  MappingMode,
  MappingTargetKind,
  NAME_MAPPINGS_MAX_LIMIT,
  StoreMappingListParams,
  StoreMappingModeInfo,
} from '../services/platformMappingApi';
import {
  DANGER_GHOST_BTN, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, PRIMARY_BTN, StatusChip,
} from './shared/listPage';
import type { ChipTone } from './shared/listPage';
import { Banner, COMPACT_INPUT, GroupTitle, LINK_BTN, ROW_DANGER_BTN, SubTabs, chipClass } from './orgPages/ui';
import type { SubTab } from './orgPages/ui';

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface OptionGroupValue {
  id: string;
  code?: string;
  name: string;
  printed_name?: string;
  price: number;
  enabled?: boolean;
  is_available?: boolean;
  // Persisted from platform_option_classifications
  internal_size_id?: number | null;
  internal_size_name?: string | null;
  internal_product_id?: number | null;
  internal_product_name?: string | null;
}

interface OptionGroup {
  id: string;
  name: string;
  type: string;
  modifier_type: 'size' | 'removal' | 'extra' | 'combo' | 'ignore';
  dependency_code?: number;
  values: OptionGroupValue[];
}

interface PlatformProduct {
  id: number;
  platform_product_id: string;
  platform_product_name: string;
  platform_product_code?: string;
  platform_product_price?: number;
  internal_product_id?: number;
  internal_size_id?: number;
  internal_product_name?: string;
  internal_size_name?: string;
  is_active: boolean;
  auto_matched: boolean;
  order_count: number;
  last_seen_at?: string;
  /** Από πού ήρθε η γραμμή: manual | auto_name | catalog | order (migration 221) */
  match_source?: string | null;
  platform_metadata?: {
    has_variations?: boolean;
    has_options?: boolean;
    is_available?: boolean;
    category_name?: string;
    option_groups?: OptionGroup[];
  };
}

interface InternalProduct {
  id: number;
  name: string;
  // Τιμή εμφάνισης delivery από τον server (displayPriceSql).
  display_price?: number | string | null;
  product_type: string;
  category_name?: string;
  sizes?: MappingSize[];
}

interface ProductMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: 'efood' | 'box' | 'wolt';
}

// ─── Αντιστοίχιση με το ΟΝΟΜΑ (platform_name_mappings) ────────────────────────
//
// ΓΙΑΤΙ ΔΕΥΤΕΡΗ ΚΑΡΤΕΛΑ: efood και Wolt βγάζουν ΝΕΑ ids προϊόντων σε κάθε
// αναδημοσίευση του μενού, οπότε οι «Κωδικοί πλατφόρμας» (κλειδί = id) σβήνουν
// μόνοι τους. Το ΟΝΟΜΑ είναι το μόνο σταθερό κλειδί που μας δίνουν σήμερα: εδώ
// ο άνθρωπος αποφασίζει μία φορά ανά όνομα και η απόφαση κρατάει.

type NameMappingStatus = 'pending' | 'mapped' | 'ignored' | 'rejected' | 'orphaned';

interface NameCandidate {
  internal_product_id?: number | null;
  org_product_id?: number | null;
  name: string;
  vat?: number | null;
  score?: number;
  kind?: 'exact' | 'fuzzy' | 'history';
}

interface NameMappingRow {
  id: number;
  platform_name: string;
  display_name: string;
  normalized_name?: string;
  status: NameMappingStatus;
  match_source?: 'exact' | 'history' | 'manual' | null;
  confidence?: number | null;
  target?: { internal_product_id?: number | null; org_product_id?: number | null; name?: string; vat?: number | null } | null;
  size_mode?: 'auto' | 'fixed' | 'none';
  org_size_id?: number | null;
  fixed_size_norm?: string | null;
  candidates?: NameCandidate[] | null;
  vat_risk?: boolean;
  is_bundle_hint?: boolean;
  seen_count?: number;
  last_seen_at?: string | null;
  last_platform_price?: number | string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  /** Μόνο στις κεντρικές γραμμές (org_rows): το ΤΟΠΙΚΟ προϊόν στο οποίο λύνεται εδώ. */
  local_product_id?: number | null;
  local_product_name?: string | null;

  // ── Φάση 2α (spec §12): το προϊόν το ορίζει η ΕΠΙΛΟΓΗ του πελάτη ──────────
  // Προαιρετικά: γραμμή/server πριν τη migration 222 δεν τα στέλνει και η οθόνη
  // συμπεριφέρεται ακριβώς όπως στη φάση 1.
  target_kind?: MappingTargetKind;
  explode_components?: boolean;
  fixed_components?: MappingFixedComponent[] | null;
  seen_as_option?: boolean;
  /** «Χυμός · Smoothies» — κάτω από ποια προϊόντα εμφανίστηκε η επιλογή. */
  option_context?: string | null;
}

/** Ό,τι στέλνει ΑΥΤΗ η οθόνη στο PUT — εμβέλεια καταστήματος, άρα ποτέ org_product_id. */
interface NameUpdateBody {
  internal_product_id?: number | null;
  status?: NameMappingStatus;
  target_kind?: MappingTargetKind;
  explode_components?: boolean;
  /** Εμβέλεια καταστήματος: τα συστατικά κρατούν internal_product_id (§12.1). */
  fixed_components?: MappingFixedComponent[] | null;
}

/**
 * Αλλαγή που αγγίζει ΜΟΝΟ το σπάσιμο και τα σταθερά συστατικά (spec §12.6).
 *
 * ΓΙΑΤΙ ξεχωρίζει: η κατάσταση και ο στόχος της γραμμής μένουν ίδια, άρα δεν
 * αλλάζουν ούτε τα πλήθη ανά κατάσταση ούτε το ποιες γραμμές περνούν τα φίλτρα —
 * μια ποσότητα «×2» δεν δικαιολογεί νέο κατέβασμα ολόκληρης της ουράς.
 */
const isComponentOnlyBody = (body: NameUpdateBody): boolean => {
  const keys = Object.keys(body || {});
  return keys.length > 0 && keys.every(k => k === 'fixed_components' || k === 'explode_components');
};

/**
 * Η γραμμή που γυρίζει το PUT — ίδια μορφή με το GET (routes: storeRowShape).
 *
 * ΓΙΑΤΙ ανέχεται δύο σχήματα: ο τύπος της υπηρεσίας επιτρέπει και `{ row }`, και
 * ένας παλιότερος server μπορεί να μη στέλνει γραμμή καθόλου. Δεχόμαστε ΜΟΝΟ τη
 * γραμμή που ζητήσαμε (ίδιο id)· σε οτιδήποτε άλλο γυρνά null και ο caller
 * ξαναδιαβάζει τη λίστα — ποτέ μισή ή λάθος ενημέρωση στην οθόνη.
 */
const rowFromUpdate = (data: any, expectedId: number): NameMappingRow | null => {
  const row = data && typeof data === 'object'
    ? (data.row && typeof data.row === 'object' ? data.row : data)
    : null;
  return row && Number(row.id) === Number(expectedId) ? (row as NameMappingRow) : null;
};

interface NameMappingCounts {
  pending?: number;
  mapped?: number;
  ignored?: number;
  rejected?: number;
  orphaned?: number;
}

/**
 * Το auto-map γράφει σε ΔΥΟ πίνακες, οπότε ο πίνακας αποτελέσματος έχει δύο
 * ομάδες: «Κωδικοί πλατφόρμας» (platform_product_mappings — σβήνουν σε κάθε
 * αναδημοσίευση μενού) και «Ονόματα» (platform_name_mappings — μένουν).
 */
interface AutoMapResult {
  // Ομάδα «Κωδικοί πλατφόρμας»
  productsMatched: number;
  sizesMatched: number;
  productsUnmatched: number;
  productsAmbiguous: number;
  suggested: number;
  // Ομάδα «Ονόματα»
  nameHarvested: number;
  nameSuggested: number;
  nameAutoMapped: number;
}

const NAME_STATUSES: NameMappingStatus[] = ['pending', 'mapped', 'ignored', 'rejected', 'orphaned'];

const NAME_STATUS_LABEL_KEY: Record<string, string> = {
  pending:  'products:nameStatusPending',
  mapped:   'products:nameStatusMapped',
  ignored:  'products:nameStatusIgnored',
  rejected: 'products:nameStatusRejected',
  orphaned: 'products:nameStatusOrphaned',
};

/**
 * Η κατάσταση ως StatusChip: ό,τι θέλει άνθρωπο (εκκρεμή, ορφανά) πορτοκαλί, το αποφασισμένο
 * πράσινο, το «Λάθος» κόκκινο, το αγνοημένο χωρίς χρώμα — το νόημα το λέει πάντα και το κείμενο.
 */
const NAME_STATUS_TONE: Record<string, ChipTone> = {
  pending:  'warn',
  mapped:   'ok',
  ignored:  'muted',
  rejected: 'danger',
  orphaned: 'warn',
};

/**
 * ΦΑΣΗ 2α (spec §12.6): τι ΕΙΝΑΙ η γραμμή. Το 'bundle' της φάσης 1 δεν
 * προσφέρεται προς επιλογή — μπορεί όμως να υπάρχει σε γραμμή της βάσης.
 */
const NAME_KIND_OPTIONS: MappingTargetKind[] = ['product', 'by_option', 'ignore'];

const NAME_KIND_LABEL_KEY: Record<string, string> = {
  product:   'products:nameKindProduct',
  by_option: 'products:nameKindByOption',
  ignore:    'products:nameKindIgnore',
  bundle:    'products:nameKindBundle',
};

// Οι ποσότητες των σταθερών συστατικών: απλός βρόχος γιατί το build στοχεύει es5.
const COMPONENT_QUANTITIES: number[] = [];
for (let n = 1; n <= MAX_COMPONENT_QUANTITY; n++) COMPONENT_QUANTITIES.push(n);

/** match_source της γραμμής ΟΝΟΜΑΤΟΣ (platform_name_mappings). */
const NAME_SOURCE_LABEL_KEY: Record<string, string> = {
  exact:   'products:nameSourceExact',
  history: 'products:nameSourceHistory',
  manual:  'products:nameSourceManual',
};

/** match_source της γραμμής ΚΩΔΙΚΟΥ (platform_product_mappings, migration 221). */
const CODE_SOURCE_LABEL_KEY: Record<string, string> = {
  manual:    'products:codeSourceManual',
  auto_name: 'products:codeSourceAutoName',
  catalog:   'products:codeSourceCatalog',
  order:     'products:codeSourceOrder',
};

/**
 * Ημερομηνία στα ελληνικά (ώρα Ελλάδας, όπως οι υπόλοιπες σελίδες της νέας εμφάνισης)· κενό όταν η
 * τιμή λείπει ή δεν είναι έγκυρη.
 */
const formatShortDate = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', year: 'numeric' });
};

/** Το μήνυμα του backend (π.χ. το 403 των κεντρικών) πριν πέσουμε σε γενικό κείμενο. */
const apiErrorMessage = mappingErrorText;

/** Μόνιμο μήνυμα της καρτέλας «Ονόματα» (το toast φεύγει σε 2,5″ και χάνεται). */
interface NameNotice { kind: 'ok' | 'warn' | 'err'; text: string }

/** Ετικέτες λειτουργίας — ίδια σειρά με το spec §1 κανόνας 8 (off | shadow | on). */
const NAME_MODES: MappingMode[] = ['off', 'shadow', 'on'];

const NAME_MODE_LABEL_KEY: Record<MappingMode, string> = {
  off:    'products:nameModeOff',
  shadow: 'products:nameModeShadow',
  on:     'products:nameModeOn',
};

/** Δεχόμαστε ΜΟΝΟ γνωστές τιμές λειτουργίας — άγνωστη τιμή = καμία ρύθμιση. */
const asMode = (value: any): MappingMode | null => (
  value === 'off' || value === 'shadow' || value === 'on' ? value : null
);

/** Η ισχύουσα λειτουργία: «Ενεργό» πράσινο, η «σκιά» πορτοκαλί (καταγράφει, δεν εφαρμόζει). */
const NAME_MODE_TONE: Record<MappingMode, ChipTone> = {
  off:    'muted',
  shadow: 'warn',
  on:     'ok',
};

const PLATFORM_NAMES: Record<string, string> = { efood: 'e-food', box: 'BOX', wolt: 'Wolt' };

// ─── Κοινά κομμάτια της όψης ──────────────────────────────────────────────────

/**
 * Διακόπτης με κουμπιά δίπλα-δίπλα (λειτουργία, φίλτρο κωδικών): ίδιο σχήμα με την «Περίοδο» του
 * «Πίνακα» (.lp-seg) — το πατημένο «ανεβαίνει» πάνω στη γκρι λωρίδα. Ξεχωρίζει έτσι από τις
 * καρτέλες (SubTabs), που έχουν το απαλό χρώμα της εφαρμογής.
 */
const SEG_WRAP = 'inline-flex max-w-full flex-wrap gap-0.5 rounded-xl bg-surface-3 p-1 dark:bg-surface-0';
const segBtn = (on: boolean): string => `inline-flex h-9 items-center whitespace-nowrap rounded-[9px] px-3 text-[13px] transition-colors `
  + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed '
  + (on
    ? 'bg-surface-1 font-semibold text-t1 shadow-sm dark:bg-surface-3'
    : 'font-medium text-t2 hover:text-t1 disabled:opacity-50');

/** Πλάτη του σκελετού φόρτωσης (όπως ο DataTable): γραμμές που μοιάζουν με κείμενο, όχι ίδιες. */
const SKELETON_WIDTHS = ['64%', '46%', '78%', '38%', '56%', '70%'];

/**
 * Ίδιες τιμές με το utils/adminPriceDisplay (τιμή delivery του προϊόντος / του μεγέθους — οι
 * πλατφόρμες είναι delivery), γραμμένες με το formatMoney: «2,60 €» στη νέα εμφάνιση, όπως κάθε
 * άλλο ποσό αυτής της οθόνης.
 */
const productPriceLabel = (ip: InternalProduct): string => formatMoney(mappingProductPrice(ip));
const sizeLabel = (size: MappingSize): string =>
  `${size.name} (${formatMoney(sizeChannelPrice(size as Parameters<typeof sizeChannelPrice>[0], 'delivery'))})`;

/** Αναζήτηση με το εικονίδιο μέσα στο πεδίο — ίδια όψη με την αναζήτηση του «Πίνακα». */
const SearchField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  className?: string;
}> = ({ value, onChange, placeholder, label, className = '' }) => (
  <div className={`relative ${className}`.trim()}>
    <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-t4" aria-hidden="true" />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className={`${INPUT_CLS} pl-10`}
    />
  </div>
);

/** Το πορτοκαλί πλήθος δίπλα στο όνομα της καρτέλας· οι αναγνώστες οθόνης ακούνε «(3 εκκρεμούν)». */
const PendingBadge: React.FC<{ count: number }> = ({ count }) => {
  const { t } = useTranslation(['products']);
  return (
    <StatusChip tone="warn">
      <span className="sr-only"> (</span>
      {count}
      <span className="sr-only">{` ${t('products:pending')})`}</span>
    </StatusChip>
  );
};

/** Σκελετός γραμμών λίστας όσο φορτώνει (αντί για το παλιό εικονίδιο που γύριζε). */
const skeletonLines = (i: number) => (
  <>
    <span className="block h-3 rounded-md bg-surface-3 motion-safe:animate-pulse" style={{ width: SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] }} />
    <span className="mt-2.5 block h-2.5 rounded-md bg-surface-3 motion-safe:animate-pulse" style={{ width: SKELETON_WIDTHS[(i + 2) % SKELETON_WIDTHS.length] }} />
  </>
);

// ─── SearchableSelect ─────────────────────────────────────────────────────────

interface SelectOption { id: number; label: string; sublabel?: string; category?: string; }

/**
 * Το κοντινότερο στοιχείο που κυλά: εκεί «κόβεται» η λίστα του επιλογέα. Μετράμε μέσα σε αυτό (και
 * όχι με το window.innerHeight) γιατί η εφαρμογή κάνει zoom στο <html> — δύο rects του ίδιου εγγράφου
 * είναι πάντα στις ίδιες μονάδες, το innerHeight όχι.
 */
const scrollParentOf = (el: HTMLElement): HTMLElement | null => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
};

/** Τα προϊόντα του καταστήματος ως επιλογές — ίδιες σε κάθε επιλογέα της οθόνης. */
const productOptions = (internalProducts: InternalProduct[], noCategory: string): SelectOption[] =>
  internalProducts.map(ip => ({
    id: ip.id,
    label: ip.name,
    sublabel: productPriceLabel(ip),
    category: ip.category_name || noCategory,
  }));

/**
 * Επιλογέας προϊόντος με αναζήτηση και ομάδες ανά κατηγορία. Ίδια συμπεριφορά με πριν: πάτημα →
 * πεδίο αναζήτησης, κλικ σε επιλογή → αλλαγή, «×» → καθαρισμός, κλικ έξω → κλείνει (η αναζήτηση
 * μένει για το επόμενο άνοιγμα). Νέα όψη: το πεδίο όπως το INPUT_CLS, η λίστα σε surface-1 με λεπτό
 * περίγραμμα και σκιά, η τρέχουσα επιλογή στο απαλό χρώμα της εφαρμογής.
 *
 * Με το πληκτρολόγιο: ↑/↓ περπατούν τη λίστα, Enter διαλέγει ΜΟΝΟ ό,τι φωτίστηκε με τα βελάκια (ένα
 * τυχαίο Enter στην αναζήτηση δεν γράφει αντιστοίχιση), Esc κλείνει ΜΟΝΟ τη λίστα — το συρτάρι από
 * κάτω αγνοεί Esc που έχει ήδη χειριστεί κάποιος (preventDefault, όπως το DateRange).
 */
const SearchableSelect: React.FC<{
  options: SelectOption[];
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
  className?: string;
  /** id της ετικέτας πάνω από τον επιλογέα: το όνομά του για τους αναγνώστες οθόνης. */
  labelledBy?: string;
}> = ({ options, value, onChange, placeholder, className = '', labelledBy }) => {
  const { t } = useTranslation(['products', 'common']);
  const uid = useId();
  const listId = `${uid}-list`;
  const valueId = `${uid}-value`;
  const defaultPlaceholder = placeholder ?? t('common:searchPlaceholder');
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const [active, setActive] = useState(-1);
  // Κοντά στο κάτω μέρος της περιοχής που κυλά η λίστα ανοίγει προς τα πάνω — αλλιώς θα κοβόταν
  // στο κάτω άκρο της (λίγο πριν από τα κουμπιά του συρταριού) και θα ήθελε κύλιση για να φανεί.
  const [dropUp, setDropUp] = useState(false);
  const ref                  = useRef<HTMLDivElement>(null);
  const triggerRef           = useRef<HTMLButtonElement>(null);
  // Μετά από επιλογή ή Esc το focus γυρίζει στον επιλογέα: το πεδίο αναζήτησης έχει φύγει από τη
  // σελίδα, και χωρίς αυτό το Tab θα ξεκινούσε από την αρχή του συρταριού.
  const refocus              = useRef(false);
  const selected             = options.find(o => o.id === value);

  const filtered = options.filter(o => {
    const q = query.toLowerCase();
    return o.label.toLowerCase().includes(q) ||
           o.sublabel?.toLowerCase().includes(q) ||
           o.category?.toLowerCase().includes(q);
  });

  const grouped = filtered.reduce((acc, o) => {
    const cat = o.category || t('products:other');
    (acc[cat] = acc[cat] || []).push(o);
    return acc;
  }, {} as Record<string, SelectOption[]>);
  // Η σειρά όπως ζωγραφίζεται (ανά κατηγορία): σε αυτήν περπατούν τα βελάκια.
  const flat = ([] as SelectOption[]).concat(...Object.values(grouped));
  const optionId = (id: number) => `${uid}-opt-${id}`;
  const activeId = open && active >= 0 && active < flat.length ? optionId(flat[active].id) : undefined;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open || !refocus.current) return;
    refocus.current = false;
    triggerRef.current?.focus();
  }, [open]);

  const openList = () => {
    if (!open && ref.current) {
      const box = ref.current.getBoundingClientRect();
      const scroller = scrollParentOf(ref.current);
      const area = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
      const below = area.bottom - box.bottom;
      const above = box.top - area.top;
      // Η λίστα είναι ως 224px (max-h-56): προς τα πάνω μόνο όταν από κάτω δεν χωρά και από πάνω υπάρχει περισσότερος χώρος.
      setDropUp(below < 240 && above > below);
    }
    setActive(-1);
    setOpen(true);
  };

  const close = (backToTrigger: boolean) => {
    refocus.current = backToTrigger;
    setOpen(false);
    setActive(-1);
  };

  const pick = (id: number) => {
    onChange(id);
    setQuery('');
    close(true);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (flat.length === 0) return;
      const next = event.key === 'ArrowDown'
        ? (active + 1) % flat.length
        : (active <= 0 ? flat.length - 1 : active - 1);
      setActive(next);
      document.getElementById(optionId(flat[next].id))?.scrollIntoView?.({ block: 'nearest' });
      return;
    }
    if (event.key === 'Enter' && active >= 0 && active < flat.length) {
      event.preventDefault();
      pick(flat[active].id);
    }
  };

  // Tab έξω από τον επιλογέα κλείνει τη λίστα· το κλικ σε επιλογή δεν μετρά ως «έξω» (η λίστα κρατά
  // το focus στο πεδίο με preventDefault στο mousedown), ούτε το «×» που είναι μέσα του.
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && ref.current?.contains(next)) return;
    setOpen(false);
    setActive(-1);
  };

  return (
    <div ref={ref} className={`relative ${className}`.trim()}>
      <div
        className="flex min-h-[44px] w-full cursor-pointer items-center gap-2 rounded-xl border border-line-strong bg-surface-2 px-3.5 text-sm text-t1 transition focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50"
        onClick={openList}
      >
        {open ? (
          <input
            autoFocus
            value={query}
            onChange={e => { setQuery(e.target.value); setActive(-1); }}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onClick={e => e.stopPropagation()}
            placeholder={defaultPlaceholder}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeId}
            aria-labelledby={labelledBy}
            aria-label={labelledBy ? undefined : defaultPlaceholder}
            className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-t1 placeholder-t4 outline-none"
          />
        ) : (
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-labelledby={labelledBy ? `${labelledBy} ${valueId}` : undefined}
            className="min-w-0 flex-1 truncate py-2.5 text-left outline-none"
          >
            <span id={valueId} className={selected ? 'text-t1' : 'text-t4'}>
              {selected ? selected.label : defaultPlaceholder}
            </span>
          </button>
        )}
        {value !== null && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onChange(null); setQuery(''); }}
            aria-label={t('common:clear')}
            title={t('common:clear')}
            className="-mr-1.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-t3 transition-colors hover:bg-surface-3 hover:text-t1"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {!open && <ChevronDown className="h-4 w-4 shrink-0 text-t4" aria-hidden="true" />}
      </div>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-labelledby={labelledBy}
          aria-label={labelledBy ? undefined : defaultPlaceholder}
          onMouseDown={e => e.preventDefault()}
          className={`absolute left-0 right-0 z-50 max-h-56 overflow-y-auto rounded-xl border border-line bg-surface-1 py-1 shadow-lg dark:shadow-black/40 ${
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-t3">{t('products:noResultsFound')}</div>
          ) : (
            Object.entries(grouped).map(([cat, opts]) => (
              <div key={cat} role="group" aria-label={cat}>
                <div aria-hidden="true" className="sticky top-0 z-[1] bg-surface-1 px-3 pb-1 pt-2 text-[11.5px] font-bold uppercase tracking-[0.06em] text-t3">
                  {cat}
                </div>
                {opts.map(o => {
                  const isSelected = o.id === value;
                  const isActive = activeId === optionId(o.id);
                  return (
                    <div
                      key={o.id}
                      id={optionId(o.id)}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => pick(o.id)}
                      className={`flex cursor-pointer items-baseline gap-2 px-3 py-2 text-sm transition-colors ${
                        isSelected ? 'bg-brand-soft text-t1' : isActive ? 'bg-surface-3 text-t1' : 'text-t2 hover:bg-surface-2'
                      }`}
                    >
                      <span className="min-w-0 flex-1 break-words font-medium">{o.label}</span>
                      {' '}
                      {o.sublabel && <span className="shrink-0 text-xs tabular-nums text-t3">{o.sublabel}</span>}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── ValueMappingRow ──────────────────────────────────────────────────────────

const ValueMappingRow: React.FC<{
  value: OptionGroupValue;
  groupId: string;
  groupName: string;
  internalSizes: MappingSize[];
  onSave: (optionId: string, groupId: string, groupName: string, optionName: string, modifierType: string, sizeId: number | null, productId: number | null) => void;
}> = ({ value, groupId, groupName, internalSizes, onSave }) => {
  const { t } = useTranslation(['products', 'common']);
  const [sizeId, setSizeId] = useState<number | null>(value.internal_size_id ?? null);

  // Reset when the value prop changes (e.g. different product selected)
  useEffect(() => {
    setSizeId(value.internal_size_id ?? null);
  }, [value.id, value.internal_size_id]);

  const handleSizeChange = (id: number | null) => {
    setSizeId(id);
    onSave(value.id, groupId, groupName, value.name, 'size', id, null);
  };

  return (
    <div className="flex items-center gap-3 py-2">
      {/* Όνομα επιλογής της πλατφόρμας + επιπλέον τιμή */}
      <div className="flex w-40 min-w-0 items-baseline gap-1.5">
        <span className="truncate text-sm text-t2">{value.name}</span>
        {value.price > 0 && <span className="shrink-0 text-xs tabular-nums text-t3">+{formatMoney(Number(value.price))}</span>}
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-t4" aria-hidden="true" />
      {/* Μέγεθος του δικού μας προϊόντος */}
      <div className="min-w-0 flex-1">
        {internalSizes.length > 0 ? (
          <select
            value={sizeId || ''}
            onChange={e => handleSizeChange(e.target.value ? parseInt(e.target.value) : null)}
            aria-label={value.name}
            className={`${COMPACT_INPUT} w-full pr-7`}
          >
            <option value="">{t('products:selectSize')}</option>
            {internalSizes.map(s => (
              <option key={s.id} value={s.id}>
                {sizeLabel(s)}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-t4">{t('products:noInternalSizes')}</span>
        )}
      </div>
    </div>
  );
};

// ─── OptionGroupCard ──────────────────────────────────────────────────────────

const OptionGroupCard: React.FC<{
  group: OptionGroup;
  internalProduct: InternalProduct | undefined;
  onClassificationChange: (optionId: string, groupId: string, groupName: string, optionName: string, modifierType: string, sizeId?: number | null, productId?: number | null) => void;
}> = ({ group, internalProduct, onClassificationChange }) => {
  const { t } = useTranslation(['products', 'common']);
  // Size is the only classification that drives anything (resolves the order's
  // size to a native product_size). Everything else passes through as text, so
  // we expose a single toggle instead of the old 5-way modifier picker.
  const [isSize, setIsSize] = useState<boolean>((group.modifier_type || '') === 'size');

  useEffect(() => {
    setIsSize((group.modifier_type || '') === 'size');
  }, [group.id, group.modifier_type]);

  const internalSizes = internalProduct?.sizes || [];

  const toggleSize = (next: boolean) => {
    setIsSize(next);
    // Persist size / not-size for every value in the group.
    for (const val of group.values) {
      onClassificationChange(val.id, group.id, group.name, val.name,
        next ? 'size' : 'extra',
        next ? (val.internal_size_id ?? null) : null,
        null
      );
    }
  };

  return (
    <div className="py-3">
      {/* Κεφαλίδα ομάδας: όνομα, πρώτες επιλογές, διακόπτης «Μέγεθος» */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-t1">{group.name}</p>
          <p className="mt-0.5 text-xs text-t3">
            {t('products:choicesCount', { count: group.values.length })}
            {group.values.slice(0, 3).map(v => ` · ${v.name}`).join('')}
            {group.values.length > 3 ? ` · +${group.values.length - 3}` : ''}
          </p>
        </div>
        <button
          type="button"
          aria-pressed={isSize}
          onClick={() => toggleSize(!isSize)}
          className={`${chipClass(isSize)} shrink-0`}
        >
          <Ruler className="h-4 w-4" aria-hidden="true" />
          {t('products:modifierSize')}
        </button>
      </div>

      {/* Μέγεθος → μέγεθος του δικού μας προϊόντος· αλλιώς οι επιλογές περνούν ως κείμενο */}
      {isSize ? (
        <div className="mt-1.5 divide-y divide-line">
          {group.values.map(val => (
            <ValueMappingRow
              key={val.id}
              value={val}
              groupId={group.id}
              groupName={group.name}
              internalSizes={internalSizes}
              onSave={onClassificationChange}
            />
          ))}
        </div>
      ) : (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {group.values.map(v => (
            <span key={v.id} className="inline-flex items-center rounded-md bg-surface-3 px-2 py-0.5 text-xs text-t2">
              {v.name}{v.price > 0 ? ` +${formatMoney(Number(v.price))}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── ProductDetail (δεξιά στήλη των «Κωδικών») ────────────────────────────────

const ProductDetail: React.FC<{
  product: PlatformProduct;
  internalProducts: InternalProduct[];
  platform: string;
  suggestion?: { id: number; name: string; score: number };
  onMappingChange: (product: PlatformProduct, productId: number | null) => void;
  onClassificationChange: (optionId: string, groupId: string, groupName: string, optionName: string, modifierType: string, sizeId?: number | null, productId?: number | null) => void;
}> = ({ product, internalProducts, platform, suggestion, onMappingChange, onClassificationChange }) => {
  const { t } = useTranslation(['products', 'common']);
  const labelId = useId();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(product.internal_product_id ?? null);

  useEffect(() => {
    setSelectedProductId(product.internal_product_id ?? null);
  }, [product.id, product.internal_product_id]);

  const selectedInternal = internalProducts.find(p => p.id === selectedProductId);
  const optionGroups = product.platform_metadata?.option_groups || [];

  const handleProductChange = (id: number | null) => {
    setSelectedProductId(id);
    onMappingChange(product, id);
  };

  return (
    <div className="space-y-5" data-testid="mapping-product-detail">
      {/* Το προϊόν όπως το στέλνει η πλατφόρμα */}
      <div>
        <div className="flex items-start justify-between gap-3">
          <h3 className="min-w-0 text-base font-semibold leading-snug text-t1">{product.platform_product_name}</h3>
          <div className="flex shrink-0 items-center gap-2">
            {product.platform_product_price != null && (
              <span className="text-sm font-semibold tabular-nums text-t1">{formatMoney(Number(product.platform_product_price))}</span>
            )}
            {product.platform_product_code && (
              <StatusChip tone="muted">{product.platform_product_code}</StatusChip>
            )}
          </div>
        </div>
        {product.platform_metadata?.category_name && (
          <p className="mt-0.5 text-xs text-t3">{PLATFORM_NAMES[platform]} · {product.platform_metadata.category_name}</p>
        )}
      </div>

      {/* Το δικό μας προϊόν */}
      <div>
        <span id={labelId} className="mb-1.5 block text-sm font-medium text-t2">{t('products:internalProduct')}</span>
        <SearchableSelect
          options={productOptions(internalProducts, t('products:noCategory'))}
          value={selectedProductId}
          onChange={handleProductChange}
          placeholder={t('products:selectInternalProduct')}
          labelledBy={labelId}
        />
        {!selectedProductId && suggestion && (
          <button
            type="button"
            onClick={() => handleProductChange(suggestion.id)}
            className={`${chipClass(false)} mt-2 max-w-full py-1.5 text-left`}
            title={t('products:applySuggestion')}
          >
            <Lightbulb className="h-4 w-4 shrink-0 text-t3" aria-hidden="true" />
            <span className="min-w-0">
              {t('products:suggestion')}: <b className="font-semibold text-t1">{suggestion.name}</b> ({suggestion.score}%)
            </span>
            {' '}
            <span className="shrink-0 font-semibold text-brand">{t('products:apply')}</span>
          </button>
        )}
        {selectedInternal && (
          <p className="mt-2 flex flex-wrap items-center gap-x-1.5 text-xs text-t3">
            <Check className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
            <span className="text-t2">{selectedInternal.name}</span>
            {selectedInternal.sizes && selectedInternal.sizes.length > 0 && (
              <span className="text-t4">{t('products:sizesAvailable', { count: selectedInternal.sizes.length })}</span>
            )}
          </p>
        )}
      </div>

      {/* Ομάδες επιλογών της πλατφόρμας */}
      <div>
        {optionGroups.length === 0 ? (
          <p className="py-6 text-center text-sm text-t3">
            {selectedProductId ? t('products:noOptionGroups') : t('products:selectInternalFirst')}
          </p>
        ) : (
          <>
            <GroupTitle>
              {t('products:optionGroups')} <span className="text-t4">({optionGroups.length})</span>
            </GroupTitle>
            <div className="mt-1 divide-y divide-line">
              {optionGroups.map(group => (
                <OptionGroupCard
                  key={group.id}
                  group={group}
                  internalProduct={selectedInternal}
                  onClassificationChange={onClassificationChange}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Καρτέλα «Ονόματα» ────────────────────────────────────────────────────────

/**
 * Η κατάσταση της γραμμής. ΓΙΑΤΙ ξεχωριστή ετικέτα για το 'by_option': μια
 * γραμμή «ανά επιλογή» είναι αποφασισμένη (status 'mapped') αλλά ΔΕΝ δείχνει σε
 * προϊόν — «Αντιστοιχισμένο» χωρίς προϊόν δίπλα διαβάζεται ως σφάλμα.
 */
const NameStatusChip: React.FC<{ status: string; kind?: MappingTargetKind }> = ({ status, kind }) => {
  const { t } = useTranslation(['products', 'common']);
  const byOption = kind === 'by_option' && status === 'mapped';
  return (
    <StatusChip tone={byOption ? 'brand' : (NAME_STATUS_TONE[status] || NAME_STATUS_TONE.pending)}>
      {byOption
        ? t('products:nameKindByOption')
        : t(NAME_STATUS_LABEL_KEY[status] || NAME_STATUS_LABEL_KEY.pending)}
    </StatusChip>
  );
};

/**
 * Οι ρυθμίσεις μιας γραμμής «Ανά επιλογή» στην εμβέλεια ΚΑΤΑΣΤΗΜΑΤΟΣ (spec §12.6).
 *
 * ΓΙΑΤΙ το σπάσιμο είναι κλειστό από προεπιλογή: η απόδειξη παύει να έχει μία
 * γραμμή combo και αποκτά N γραμμές συστατικών με τιμές που ΠΡΟΚΥΠΤΟΥΝ από τη
 * μοιρασιά του συνόλου. Το άθροισμα μένει ίδιο, αλλά αλλάζει η εικόνα του
 * παραστατικού και οι συντελεστές ΦΠΑ ανά είδος — το αποφασίζει άνθρωπος.
 */
const NameByOptionPanel: React.FC<{
  row: NameMappingRow;
  internalProducts: InternalProduct[];
  canWrite: boolean;
  /** Τρέχει ήδη αποθήκευση για ΑΥΤΗ τη γραμμή — τα χειριστήρια κλειδώνουν. */
  busy: boolean;
  onUpdate: (row: NameMappingRow, body: NameUpdateBody) => void;
}> = ({ row, internalProducts, canWrite, busy, onUpdate }) => {
  const { t } = useTranslation(['products', 'common']);
  const components = parseFixedComponents(row.fixed_components);
  const explode = row.explode_components === true;
  const full = components.length >= MAX_FIXED_COMPONENTS;

  // Πρώτα το όνομα που στέλνει το backend (το διαβάζει για ΑΥΤΟ το κατάστημα),
  // μετά ο τοπικός κατάλογος της οθόνης. Χωρίς κανένα από τα δύο το συστατικό
  // ΔΕΝ υπάρχει εδώ — ο resolver θα αρνηθεί να σπάσει το combo, οπότε το λέμε.
  const productName = (id: number | null, fromServer?: string | null): { name: string; missing: boolean } => {
    const found = id != null ? internalProducts.find(p => p.id === id) : undefined;
    const name = fromServer || found?.name || (id != null ? `#${id}` : '—');
    return { name, missing: !fromServer && !found };
  };

  const addComponent = (id: number | null) => {
    if (id == null || full || busy) return;
    // Δύο φορές το ίδιο προϊόν = μία γραμμή με μεγαλύτερη ποσότητα (έτσι τα
    // ενώνει και ο resolver), όχι δύο ίδιες γραμμές στην απόδειξη.
    const index = components.findIndex(c => fixedComponentId(c) === id);
    const next = index !== -1
      ? components.map((c, i) => (i === index
        ? { ...c, quantity: Math.min((Number(c.quantity) || 1) + 1, MAX_COMPONENT_QUANTITY) }
        : c))
      : components.concat([{ internal_product_id: id, quantity: 1 }]);
    onUpdate(row, { fixed_components: next });
  };

  // Όχι χρωματιστό κουτί: μια λεπτή γραμμή αριστερά δείχνει ότι αυτά ανήκουν στη γραμμή από πάνω.
  return (
    <div className="mt-3 space-y-3 border-l-2 border-line pl-3">
      {/* ΓΙΑΤΙ κλειδώνουν όσο τρέχει η αποθήκευση: η τιμή του διακόπτη και του
          επιλογέα βγαίνει από τη ΓΡΑΜΜΗ. Μια δεύτερη αλλαγή πάνω στην πρώτη θα
          έστελνε παλιά συστατικά ή θα «γυρνούσε» μόνη της μόλις απαντήσει ο
          server — ο χρήστης θα έβλεπε την επιλογή του να αναιρείται σιωπηλά. */}
      <label className={`flex items-start gap-2.5 ${canWrite && !busy ? 'cursor-pointer' : 'cursor-default'}`}>
        <input
          type="checkbox"
          checked={explode}
          disabled={!canWrite || busy}
          onChange={e => onUpdate(row, { explode_components: e.target.checked })}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--brand))] disabled:opacity-50"
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-t1">{t('products:nameExplode')}</span>
          <span className="mt-0.5 block text-xs text-t3">{t('products:nameExplodeWarning')}</span>
        </span>
      </label>

      {/* Σταθερά συστατικά: ό,τι μπαίνει ΠΑΝΤΑ στο σπάσιμο και δεν υπάρχει σε
          καμία επιλογή του πελάτη (π.χ. το νερό του combo πρωινού). */}
      <div>
        <GroupTitle>
          {t('products:nameFixedComponents')} <span className="text-t4">({components.length})</span>
        </GroupTitle>
        {!explode && components.length > 0 && (
          <p className="mt-1 text-xs text-t3">{t('products:nameFixedComponentsOnlyExplode')}</p>
        )}

        {components.length === 0 ? (
          <p className="mt-1 text-xs text-t3">{t('products:nameFixedComponentsEmpty')}</p>
        ) : (
          <ul className="mt-1 divide-y divide-line">
            {components.map((component, index) => {
              const id = fixedComponentId(component);
              const { name, missing } = productName(id, component.name);
              return (
                <li key={`${row.id}-fixed-${id ?? index}`} className="flex items-center gap-2 py-1.5 text-sm">
                  <span
                    className="min-w-0 flex-1 truncate text-t2"
                    title={missing ? t('products:nameFixedMissing', { name }) : name}
                  >
                    {name}
                  </span>
                  {missing && (
                    <StatusChip tone="danger" title={t('products:nameFixedMissing', { name })}>
                      {t('products:nameFixedMissingBadge')}
                    </StatusChip>
                  )}
                  {canWrite ? (
                    <select
                      value={component.quantity}
                      disabled={busy}
                      aria-label={t('products:nameFixedQuantity')}
                      onChange={e => onUpdate(row, {
                        fixed_components: components.map((c, i) => (
                          i === index ? { ...c, quantity: Number(e.target.value) } : c
                        )),
                      })}
                      className={`${COMPACT_INPUT} shrink-0 pr-7 tabular-nums`}
                    >
                      {COMPONENT_QUANTITIES.map(n => <option key={n} value={n}>×{n}</option>)}
                    </select>
                  ) : (
                    <span className="shrink-0 tabular-nums text-t3">×{component.quantity}</span>
                  )}
                  {canWrite && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onUpdate(row, {
                        fixed_components: components.filter((_, i) => i !== index),
                      })}
                      aria-label={t('products:nameFixedRemove')}
                      title={t('products:nameFixedRemove')}
                      className={ROW_DANGER_BTN}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {canWrite && !full && (
          <div className="mt-2">
            <SearchableSelect
              options={productOptions(internalProducts, t('products:noCategory'))}
              value={null}
              onChange={addComponent}
              placeholder={t('products:nameFixedAdd')}
            />
          </div>
        )}
        {canWrite && full && (
          <p className="mt-2 text-xs text-t3">
            {t('products:nameFixedLimit', { count: MAX_FIXED_COMPONENTS })}
          </p>
        )}
      </div>
    </div>
  );
};

const NameMappingRowCard: React.FC<{
  row: NameMappingRow;
  internalProducts: InternalProduct[];
  canWrite: boolean;
  busy: boolean;
  onUpdate: (row: NameMappingRow, body: NameUpdateBody) => void;
}> = ({ row, internalProducts, canWrite, busy, onUpdate }) => {
  const { t } = useTranslation(['products', 'common']);
  const candidates  = Array.isArray(row.candidates) ? row.candidates : [];
  const targetId    = row.target?.internal_product_id ?? null;
  const lastSeen    = formatShortDate(row.last_seen_at);
  const price       = row.last_platform_price != null ? Number(row.last_platform_price) : null;
  const sourceKey   = row.match_source ? NAME_SOURCE_LABEL_KEY[row.match_source] : null;
  const kind        = rowTargetKind(row);
  const byOption    = kind === 'by_option';

  /**
   * Αλλαγή είδους (spec §12.6). ΓΙΑΤΙ το «Ανά επιλογή» γράφει ΚΑΙ status
   * 'mapped': ο resolver φορτώνει μόνο γραμμές 'mapped'/'ignored', οπότε μια
   * by_option που έμενε 'pending' δεν θα έλυνε ΠΟΤΕ επιλογή — ο χρήστης θα
   * έβλεπε τη ρύθμισή του να μην κάνει τίποτα. Ο στόχος είναι προαιρετικός εδώ.
   */
  const changeKind = (next: MappingTargetKind) => {
    if (next === kind) return;
    if (next === 'ignore')    return onUpdate(row, { target_kind: 'ignore', status: 'ignored' });
    if (next === 'by_option') return onUpdate(row, { target_kind: 'by_option', status: 'mapped' });
    // Πίσω σε προϊόν: χωρίς στόχο η γραμμή δεν είναι αντιστοιχισμένη — γυρίζει
    // στα εκκρεμή, αλλιώς θα έμενε 'mapped' με κενό προϊόν. Το σπάσιμο και τα
    // σταθερά συστατικά ΔΕΝ πειράζονται: ο resolver τα διαβάζει μόνο σε γραμμές
    // by_option και μια προσωρινή αλλαγή είδους δεν σβήνει ανθρώπινη ρύθμιση.
    // Ρητό status ΠΑΝΤΑ: μια γραμμή που ερχόταν από «Αγνόηση» έχει status
    // 'ignored' και το backend ξαναγράφει target_kind 'ignore' όταν η κατάσταση
    // λέει 'ignored' — χωρίς αυτό η αλλαγή είδους δεν θα είχε κανένα αποτέλεσμα.
    return onUpdate(row, {
      target_kind: 'product',
      status: targetId != null ? 'mapped' : 'pending',
    });
  };

  const meta = [
    t('products:nameSeenCount', { count: row.seen_count || 0 }),
    lastSeen ? t('products:nameLastSeen', { date: lastSeen }) : '',
    price != null && !Number.isNaN(price) ? formatMoney(price) : '',
    sourceKey ? t(sourceKey) : '',
  ].filter(Boolean).join(' · ');

  // Γραμμή λίστας, όχι κάρτα: λεπτή γραμμή ανάμεσα (από τη λίστα), και το ρίσκο ΦΠΑ με κόκκινη
  // λωρίδα αριστερά + ετικέτα — όχι κόκκινο φόντο σε όλη τη γραμμή.
  return (
    <li
      className={`relative py-3.5 ${busy ? 'pointer-events-none opacity-50' : ''}`}
      aria-busy={busy || undefined}
      data-testid={`name-row-${row.id}`}
    >
      {row.vat_risk && (
        <span aria-hidden="true" className="absolute -left-3 bottom-3 top-3 w-[3px] rounded-full bg-red-500" />
      )}

      {/* Όνομα όπως το στέλνει η πλατφόρμα + σήματα ρίσκου */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="min-w-0 break-words text-sm font-semibold text-t1">{row.display_name}</span>
        <NameStatusChip status={row.status} kind={kind} />
        {row.vat_risk && (
          <StatusChip tone="danger" title={t('products:nameVatRiskHint')}>{t('products:nameVatRiskBadge')}</StatusChip>
        )}
        {row.is_bundle_hint && (
          <StatusChip tone="warn" title={t('products:nameBundleHint')}>{t('products:nameBundleBadge')}</StatusChip>
        )}
      </div>

      {/* Το όνομα ήρθε από ΕΠΙΛΟΓΗ πελάτη: χωρίς τον γονέα, το «coca cola zero»
          μέσα στην ουρά δεν λέει σε ποιο προϊόν ανήκει. */}
      {row.seen_as_option && (
        <p className="mt-0.5 truncate text-xs text-t3" title={row.option_context || t('products:nameOptionOfBadge')}>
          {row.option_context
            ? t('products:nameOptionOf', { context: row.option_context })
            : t('products:nameOptionOfBadge')}
        </p>
      )}

      <p className="mt-0.5 text-xs text-t3">{meta}</p>

      {/* Είδος γραμμής: αλλάζει το νόημα όσων ακολουθούν, άρα μπαίνει από πάνω. */}
      {canWrite ? (
        <label className="mt-2 inline-flex items-center gap-2 text-xs text-t3">
          {t('products:nameKindLabel')}
          <select
            value={kind === 'bundle' ? 'product' : kind}
            onChange={e => changeKind(e.target.value as MappingTargetKind)}
            title={t('products:nameKindByOptionHint')}
            className={`${COMPACT_INPUT} pr-7`}
          >
            {NAME_KIND_OPTIONS.map(k => (
              <option key={k} value={k}>{t(NAME_KIND_LABEL_KEY[k])}</option>
            ))}
          </select>
        </label>
      ) : kind !== 'product' && (
        <p className="mt-2 text-xs text-t3">
          {t('products:nameKindLabel')} {t(NAME_KIND_LABEL_KEY[kind] || NAME_KIND_LABEL_KEY.product)}
        </p>
      )}

      {byOption && (
        <>
          <NameByOptionPanel
            row={row}
            internalProducts={internalProducts}
            canWrite={canWrite}
            busy={busy}
            onUpdate={onUpdate}
          />
          {/* Ο στόχος μιας γραμμής «ανά επιλογή» είναι ΕΦΕΔΡΕΙΑ. */}
          <p className="mt-3 text-xs text-t3">{t('products:nameFallbackTarget')}</p>
        </>
      )}

      {/* Στόχος: τοπικό προϊόν */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <ArrowRight className="h-4 w-4 shrink-0 text-t4" aria-hidden="true" />
        {canWrite ? (
          <SearchableSelect
            className="min-w-[12rem] flex-1"
            options={productOptions(internalProducts, t('products:noCategory'))}
            value={targetId}
            onChange={id => onUpdate(row, {
              internal_product_id: id,
              // Σε γραμμή «ανά επιλογή» το προϊόν είναι ΕΦΕΔΡΕΙΑ: αν σβηστεί, η
              // γραμμή παραμένει αποφασισμένη (το είδος το λέει η επιλογή) —
              // δεν την ξαναρίχνουμε στα εκκρεμή.
              status: byOption ? 'mapped' : (id ? 'mapped' : 'pending'),
            })}
            placeholder={byOption ? t('products:nameFallbackTarget') : t('products:selectInternalProduct')}
          />
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm text-t2">
            {row.target?.name || t('products:nameNoTarget')}
          </span>
        )}
        {canWrite && row.status !== 'ignored' && (
          <button
            type="button"
            onClick={() => onUpdate(row, { status: 'ignored' })}
            title={t('products:nameIgnoreHint')}
            className={GHOST_BTN}
          >{t('products:nameIgnore')}</button>
        )}
        {canWrite && row.status !== 'rejected' && (
          <button
            type="button"
            onClick={() => onUpdate(row, { status: 'rejected' })}
            title={t('products:nameRejectHint')}
            className={DANGER_GHOST_BTN}
          >{t('products:nameReject')}</button>
        )}
        {canWrite && (row.status === 'ignored' || row.status === 'rejected') && (
          <button
            type="button"
            onClick={() => onUpdate(row, { status: 'pending' })}
            className={GHOST_BTN}
          >{t('products:nameReopen')}</button>
        )}
      </div>

      {/* Υποψήφια με ένα κλικ. ΔΕΝ εφαρμόζονται ποτέ μόνα τους (spec §1 κανόνας 2). */}
      {candidates.length > 0 && row.status !== 'mapped' && (
        <div className="mt-3">
          <GroupTitle>{t('products:nameCandidates')}</GroupTitle>
          {/* Ολόκληρο το όνομα, μία πρόταση ανά γραμμή — κομμένα ονόματα δεν ξεχωρίζουν. */}
          <div className="mt-1.5 flex flex-col items-start gap-1.5">
            {candidates.map((c, i) => (
              <button
                type="button"
                key={`${c.internal_product_id ?? c.org_product_id ?? c.name}-${i}`}
                disabled={!canWrite || c.internal_product_id == null}
                onClick={() => onUpdate(row, { internal_product_id: c.internal_product_id, status: 'mapped' })}
                title={c.name}
                className={`${chipClass(false)} max-w-full py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-40`}
              >
                <span className="whitespace-normal break-words font-medium text-t1">{c.name}</span>
                {' '}
                <span className="shrink-0 text-xs tabular-nums text-t3">
                  {c.kind === 'exact'
                    ? t('products:nameCandidateExact')
                    : c.kind === 'history'
                      ? t('products:mappingPanel.candidateHistory')
                      : `${Math.round((c.score || 0) * 100)}%`}
                </span>
                {c.vat != null && Number(c.vat) !== 13 && (
                  <>
                    {' '}
                    <span className="shrink-0 text-xs font-semibold text-red-700 dark:text-red-400">
                      {t('products:nameVatValue', { vat: c.vat })}
                    </span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </li>
  );
};

/** Κεντρική γραμμή, ΜΟΝΟ για ανάγνωση — ο οργανισμός την ορίζει, το κατάστημα τη βλέπει. */
const OrgNameRowCard: React.FC<{ row: NameMappingRow }> = ({ row }) => {
  const { t } = useTranslation(['products', 'common']);
  const targetName = row.local_product_name || row.target?.name || '';
  // Κεντρικά αντιστοιχισμένο αλλά το προϊόν δεν έχει κατέβει σε ΑΥΤΟ το κατάστημα.
  const missingLocally = row.status === 'mapped' && row.local_product_id == null;
  const kind = rowTargetKind(row);
  const byOption = kind === 'by_option';
  // Τα σταθερά συστατικά του ομίλου κρατούν ΚΕΝΤΡΙΚΑ ids: εδώ δεν υπάρχει ο
  // κεντρικός κατάλογος για να δείξουμε ονόματα — δείχνουμε το πλήθος, που
  // αρκεί για να ξέρει το κατάστημα ότι η γραμμή σπάει σε είδη.
  const fixedCount = byOption ? parseFixedComponents(row.fixed_components).length : 0;

  return (
    <li className="py-2.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="min-w-0 break-words text-sm text-t1">{row.display_name}</span>
        <NameStatusChip status={row.status} kind={kind} />
        {row.vat_risk && <StatusChip tone="danger">{t('products:nameVatRiskBadge')}</StatusChip>}
      </div>
      <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-t3">
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-t4" aria-hidden="true" />
        {missingLocally ? (
          <StatusChip tone="danger">{t('products:nameOrgMissingLocally')}</StatusChip>
        ) : (
          <span className="min-w-0 truncate">
            {targetName || (byOption ? t('products:nameKindByOption') : t('products:nameNoTarget'))}
          </span>
        )}
      </p>

      {/* Φάση 2α: τι ακριβώς αποφάσισε ο όμιλος για τη γραμμή — μόνο ανάγνωση. */}
      {byOption && (
        <p className="mt-0.5 text-xs text-t3">
          {[
            t('products:nameKindByOption'),
            row.explode_components === true ? t('products:nameExplodeBadge') : '',
            fixedCount > 0 ? t('products:nameFixedComponentsCount', { count: fixedCount }) : '',
            targetName ? t('products:nameFallbackTargetShort', { name: targetName }) : '',
          ].filter(Boolean).join(' · ')}
        </p>
      )}
      {row.seen_as_option && row.option_context && (
        <p className="mt-0.5 truncate text-xs text-t3" title={row.option_context}>
          {t('products:nameOptionOf', { context: row.option_context })}
        </p>
      )}
    </li>
  );
};

/**
 * Ισχύουσα λειτουργία του καταστήματος (+ επιλογέας για όποιον επιτρέπεται).
 *
 * ΓΙΑΤΙ ΦΑΙΝΕΤΑΙ ΕΔΩ: ο resolver διαβάζει COALESCE(store, org, 'off') — χωρίς
 * αυτή τη γραμμή ο χρήστης δουλεύει την ουρά χωρίς να ξέρει αν οι αποφάσεις του
 * ΕΦΑΡΜΟΖΟΝΤΑΙ στις παραγγελίες ή απλώς καταγράφονται. Και ΓΙΑΤΙ ΥΠΑΡΧΕΙ το
 * «Όπως ο όμιλος»: μια τοπική ρύθμιση επιβιώνει σιωπηλά σε rollback του ομίλου,
 * οπότε πρέπει να μπορεί να ΣΒΗΣΤΕΙ από εδώ ({ mode: null }).
 */
const NameModeBar: React.FC<{
  info: StoreMappingModeInfo | null;
  canManage: boolean;
  saving: boolean;
  onChange: (mode: MappingMode | null) => void;
}> = ({ info, canManage, saving, onChange }) => {
  const { t } = useTranslation(['products', 'common']);
  if (!info) return null;

  const effective: MappingMode = asMode(info.mode) || 'off';
  const override = asMode(info.store_override);
  const orgMode = asMode(info.org_mode);

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2" data-testid="name-mode-bar">
      <span className="text-sm font-medium text-t2">{t('products:nameModeLabel')}</span>
      <StatusChip tone={NAME_MODE_TONE[effective]} title={t('products:nameModeHint')}>
        {t(NAME_MODE_LABEL_KEY[effective])}
      </StatusChip>
      {override ? (
        <span className="text-xs font-semibold text-t2">{t('products:nameModeOwnBadge')}</span>
      ) : orgMode ? (
        <span className="text-xs text-t3">
          {t('products:nameModeOrgValue', { mode: t(NAME_MODE_LABEL_KEY[orgMode]) })}
        </span>
      ) : null}

      {canManage && (
        <div role="group" aria-label={t('products:nameModeLabel')} className={`${SEG_WRAP} ml-auto`}>
          {NAME_MODES.map(m => (
            <button
              key={m}
              type="button"
              aria-pressed={override === m}
              onClick={() => onChange(m)}
              disabled={saving}
              title={t('products:nameModeHint')}
              className={segBtn(override === m)}
            >{t(NAME_MODE_LABEL_KEY[m])}</button>
          ))}
          {orgMode !== null && (
            <button
              type="button"
              aria-pressed={!override}
              onClick={() => onChange(null)}
              disabled={saving || !override}
              title={t('products:nameModeFollowOrgHint')}
              className={segBtn(!override)}
            >{t('products:nameModeFollowOrg')}</button>
          )}
        </div>
      )}
    </div>
  );
};

const NameMappingsTab: React.FC<{
  rows: NameMappingRow[];
  orgRows: NameMappingRow[];
  counts: NameMappingCounts;
  loading: boolean;
  error: string | null;
  /** Μόνιμο μήνυμα (π.χ. 409 «τρέχει ήδη» / «λάθος server») — το toast δεν αρκεί. */
  notice: NameNotice | null;
  onDismissNotice: () => void;
  /** Τα φίλτρα ζουν στον γονέα: πηγαίνουν στο backend, όχι μόνο στην οθόνη. */
  statusFilter: 'all' | NameMappingStatus;
  onStatusFilter: (status: 'all' | NameMappingStatus) => void;
  query: string;
  onQuery: (query: string) => void;
  /** Φάση 2α: μόνο ονόματα που ήρθαν από ΕΠΙΛΟΓΕΣ πελατών (only_options=1). */
  optionsOnly: boolean;
  onOptionsOnly: (value: boolean) => void;
  /** true όταν το backend γύρισε ΑΚΡΙΒΩΣ όσες ζητήσαμε: λείπουν κι άλλες. */
  truncated: boolean;
  modeInfo: StoreMappingModeInfo | null;
  canManageMode: boolean;
  modeSaving: boolean;
  onChangeMode: (mode: MappingMode | null) => void;
  orgManaged: boolean;
  canWrite: boolean;
  busyId: number | null;
  suggesting: boolean;
  accepting: boolean;
  internalProducts: InternalProduct[];
  onUpdate: (row: NameMappingRow, body: NameUpdateBody) => void;
  onSuggest: () => void;
  onAcceptExact: () => void;
}> = ({
  rows, orgRows, counts, loading, error, notice, onDismissNotice,
  statusFilter, onStatusFilter, query, onQuery, optionsOnly, onOptionsOnly, truncated,
  modeInfo, canManageMode, modeSaving, onChangeMode,
  orgManaged, canWrite, busyId,
  suggesting, accepting, internalProducts, onUpdate, onSuggest, onAcceptExact
}) => {
  const { t } = useTranslation(['products', 'common']);

  // Τα πλήθη των chips έρχονται από το backend (GROUP BY χωρίς όριο), όχι από
  // τις γραμμές που κατέβηκαν — αλλιώς το «Εκκρεμεί» θα έδειχνε το όριο.
  const totalCount = NAME_STATUSES.reduce(
    (sum, s) => sum + (counts[s as keyof NameMappingCounts] ?? 0), 0
  );

  const visible = rows
    .filter(r => (statusFilter === 'all' ? true : r.status === statusFilter))
    .filter(r => {
      if (!query) return true;
      const q = query.toLowerCase();
      return (r.display_name || '').toLowerCase().includes(q) ||
             (r.target?.name || '').toLowerCase().includes(q);
    })
    // Πρώτα το ρίσκο ΦΠΑ: μια λάθος γραμμή εδώ κόβει απόδειξη με λάθος
    // συντελεστή. Μετά όσα ονόματα εμφανίζονται πιο συχνά στις παραγγελίες.
    .sort((a, b) =>
      ((b.vat_risk ? 1 : 0) - (a.vat_risk ? 1 : 0)) ||
      ((b.seen_count || 0) - (a.seen_count || 0))
    );

  return (
    <div role="tabpanel" aria-label={t('products:tabNames')} className="flex flex-col gap-3 sm:min-h-0 sm:flex-1" data-testid="mapping-names-tab">

      <NameModeBar
        info={modeInfo}
        canManage={canManageMode}
        saving={modeSaving}
        onChange={onChangeMode}
      />

      {/* Κεντρικός οργανισμός: το κατάστημα βλέπει, δεν αλλάζει. */}
      {orgManaged && (
        <Banner tone="info" icon={Building2} className="shrink-0" testId="name-org-managed">
          <p className="font-semibold text-t1">{t('products:nameOrgManagedBanner')}</p>
          <p className="mt-0.5 text-xs text-t3">
            {canWrite ? t('products:nameOrgManagedOverride') : t('products:nameOrgManagedHint')}
          </p>
        </Banner>
      )}

      {/* Εργαλειοθήκη: αναζήτηση και μαζικές ενέργειες */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <SearchField
          value={query}
          onChange={onQuery}
          placeholder={t('common:searchPlaceholder')}
          label={t('common:search')}
          className="min-w-[200px] flex-1"
        />
        {canWrite && (
          <>
            <button
              type="button"
              onClick={onAcceptExact}
              disabled={accepting}
              title={t('products:nameAcceptExactHint')}
              className={GHOST_BTN}
            >
              {accepting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {accepting ? t('products:applying') : t('products:nameAcceptExact')}
            </button>
            <button
              type="button"
              onClick={onSuggest}
              disabled={suggesting}
              title={t('products:nameCheckNowHint')}
              className={GHOST_BTN}
            >
              {suggesting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {suggesting ? t('products:applying') : t('products:nameCheckNow')}
            </button>
          </>
        )}
      </div>

      {/* Φίλτρα κατάστασης με πλήθη (πάνε στο backend) + «Μόνο επιλογές» */}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <div role="group" aria-label={t('common:status')} className="flex flex-wrap items-center gap-1.5">
          {(['all'] as Array<'all' | NameMappingStatus>).concat(NAME_STATUSES).map(s => {
            const on = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                onClick={() => onStatusFilter(s)}
                className={chipClass(on)}
              >
                {s === 'all' ? t('products:allFilter') : t(NAME_STATUS_LABEL_KEY[s])}
                {' '}
                <span className={`text-xs tabular-nums ${on ? 'opacity-75' : 'text-t4'}`}>
                  {s === 'all' ? totalCount : (counts[s as keyof NameMappingCounts] ?? rows.filter(r => r.status === s).length)}
                </span>
              </button>
            );
          })}
        </div>
        <span aria-hidden="true" className="mx-1 h-5 w-px bg-line" />
        {/* Οι επιλογές («coca cola zero») είναι ξεχωριστή δουλειά από τα
            προϊόντα: μαζεύονται από τα materials των γραμμών «ανά επιλογή». */}
        <button
          type="button"
          onClick={() => onOptionsOnly(!optionsOnly)}
          aria-pressed={optionsOnly}
          title={t('products:nameOnlyOptionsHint')}
          className={chipClass(optionsOnly)}
        >{t('products:nameOnlyOptions')}</button>
      </div>

      {/* Λίστα: κυλά μόνη της, η εργαλειοθήκη μένει από πάνω */}
      <div className="-mx-3 border-t border-line px-3 sm:min-h-0 sm:flex-1 sm:overflow-y-auto">
        {(error || notice || truncated) && (
          <div className="space-y-2 pt-3">
            {error && <Banner tone="danger" role="alert" testId="name-error">{error}</Banner>}

            {/* Μηνύματα που ΔΕΝ επιτρέπεται να φύγουν σε 2,5″: το 409 του backend λέει
                είτε «τρέχει ήδη έλεγχος» είτε «λάθος server» — και τα δύο αλλάζουν
                το τι πρέπει να κάνει ο χρήστης μετά. */}
            {notice && (
              <Banner
                tone={notice.kind === 'ok' ? 'ok' : notice.kind === 'warn' ? 'warn' : 'danger'}
                role={notice.kind === 'err' ? 'alert' : 'status'}
                onDismiss={onDismissNotice}
                dismissLabel={t('common:close')}
                testId="name-notice"
              >{notice.text}</Banner>
            )}

            {/* Σιωπηλή κοπή = χαμένη δουλειά: ο χρήστης δούλευε 200 κάρτες ενώ το chip
                έλεγε 850 και το badge δεν μηδένιζε ποτέ. Τώρα το λέμε ρητά. */}
            {truncated && (
              <Banner tone="warn" testId="name-truncated">
                {t('products:nameTruncated', { limit: NAME_MAPPINGS_MAX_LIMIT })}
              </Banner>
            )}
          </div>
        )}

        {loading ? (
          <>
            <div aria-hidden="true" className="divide-y divide-line">
              {[0, 1, 2, 3].map(i => <div key={i} className="py-4">{skeletonLines(i)}</div>)}
            </div>
            <span className="sr-only" role="status">{t('common:loading')}</span>
          </>
        ) : visible.length === 0 ? (
          // Το φιλτράρισμα γίνεται πλέον στο backend: κενή λίστα ΔΕΝ σημαίνει
          // «δεν υπάρχουν ονόματα» — μόνο τα συνολικά πλήθη το λένε αυτό.
          <EmptyState
            title={totalCount === 0 && !query ? t('products:nameListEmpty') : t('products:noProductsFound')}
            testId="name-empty"
          />
        ) : (
          <ul className="divide-y divide-line" aria-label={t('products:tabNames')}>
            {visible.map(row => (
              <NameMappingRowCard
                key={row.id}
                row={row}
                internalProducts={internalProducts}
                canWrite={canWrite}
                busy={busyId === row.id}
                onUpdate={onUpdate}
              />
            ))}
          </ul>
        )}

        {/* Κεντρικές γραμμές (μόνο ανάγνωση) */}
        {(orgManaged || orgRows.length > 0) && (
          <section className="pb-1 pt-5" data-testid="name-org-rows">
            <GroupTitle>
              {t('products:nameOrgRowsTitle')} <span className="text-t4">({orgRows.length})</span>
            </GroupTitle>
            {orgRows.length === 0 ? (
              <p className="mt-2 text-sm text-t3">{t('products:nameOrgRowsEmpty')}</p>
            ) : (
              <ul className="mt-1 divide-y divide-line">
                {orgRows.map(row => <OrgNameRowCard key={`org-${row.id}`} row={row} />)}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

// ─── Το συρτάρι ───────────────────────────────────────────────────────────────

const ProductMappingModal: React.FC<ProductMappingModalProps> = ({ isOpen, onClose, platform }) => {
  const { store, employee } = useAuth();
  const { t } = useTranslation(['products', 'common']);

  // Κατάστημα κεντρικού οργανισμού: οι αντιστοιχίσεις ορίζονται στο HQ. Γράφουν
  // μόνο super_admin/support — ίδιος κανόνας με το 403 του backend (spec §8).
  const orgManaged    = !!store?.organization_id && store?.catalog_management === 'central';
  const canWriteNames = !orgManaged || employee?.role === 'super_admin' || employee?.role === 'support';

  // Η ΛΕΙΤΟΥΡΓΙΑ (off/shadow/on) αλλάζει το τι κόβεται στην απόδειξη, οπότε είναι
  // πιο «βαριά» από μια αντιστοίχιση: υποστήριξη πάντα, ο διαχειριστής του
  // καταστήματος μόνο όταν ο κατάλογος ΔΕΝ κρατιέται κεντρικά.
  const canManageMode =
    employee?.role === 'super_admin' ||
    employee?.role === 'support' ||
    (!orgManaged && employee?.role === 'store_admin');

  // ΠΡΟΕΠΙΛΕΓΜΕΝΗ ΚΑΡΤΕΛΑ: οι «Κωδικοί πλατφόρμας» — αυτή είναι η οθόνη που ήδη
  // ξέρει ο χρήστης του καταστήματος και δεν του αλλάζουμε το σημείο εκκίνησης.
  // Στα κεντρικά καταστήματα όμως τους κωδικούς δεν τους πειράζει κανείς από εδώ,
  // οπότε εκεί μπροστά έρχονται τα «Ονόματα».
  const [activeTab,          setActiveTab]          = useState<'names' | 'codes'>(orgManaged ? 'names' : 'codes');
  const [loading,            setLoading]            = useState(false);
  const [syncing,            setSyncing]            = useState(false);
  const [autoMatching,       setAutoMatching]       = useState(false);
  const [platformProducts,   setPlatformProducts]   = useState<PlatformProduct[]>([]);
  const [internalProducts,   setInternalProducts]   = useState<InternalProduct[]>([]);
  const [selectedId,         setSelectedId]         = useState<number | null>(null);
  const [searchQuery,        setSearchQuery]        = useState('');
  const [filter,             setFilter]             = useState<'all' | 'mapped' | 'unmapped'>('all');
  const [toast,              setToast]              = useState<string | null>(null);
  // Fuzzy auto-match suggestions (platform_product_id → suggested native product). NOT auto-applied.
  const [suggestions,        setSuggestions]        = useState<Map<string, { id: number; name: string; score: number }>>(new Map());
  // Το αποτέλεσμα του auto-match ΜΕΝΕΙ στην οθόνη: είναι λίστα εργασίας (ποια
  // ονόματα θέλουν άνθρωπο), δεν διαβάζεται σε ένα toast 2,5 δευτερολέπτων.
  const [autoMapResult,      setAutoMapResult]      = useState<AutoMapResult | null>(null);

  // Καρτέλα «Ονόματα» (platform_name_mappings)
  const [nameRows,           setNameRows]           = useState<NameMappingRow[]>([]);
  const [orgNameRows,        setOrgNameRows]        = useState<NameMappingRow[]>([]);
  const [nameCounts,         setNameCounts]         = useState<NameMappingCounts>({});
  const [namesLoading,       setNamesLoading]       = useState(false);
  const [namesError,         setNamesError]         = useState<string | null>(null);
  const [nameBusyId,         setNameBusyId]         = useState<number | null>(null);
  const [nameSuggesting,     setNameSuggesting]     = useState(false);
  const [nameAccepting,      setNameAccepting]      = useState(false);
  // Τα φίλτρα πάνε στο BACKEND (δεν φιλτράρουμε μόνο ό,τι κατέβηκε): με όριο
  // γραμμών ανά κλήση, ένα φίλτρο που μένει στην οθόνη δεν φτάνει ποτέ στις
  // γραμμές που δεν κατέβηκαν.
  const [nameStatusFilter,   setNameStatusFilter]   = useState<'all' | NameMappingStatus>('pending');
  // Φάση 2α: μόνο τα ονόματα που μαζεύτηκαν από ΕΠΙΛΟΓΕΣ πελατών (only_options).
  const [nameOptionsOnly,    setNameOptionsOnly]    = useState(false);
  const [nameQuery,          setNameQuery]          = useState('');
  const [nameQueryApplied,   setNameQueryApplied]   = useState('');
  const [nameTruncated,      setNameTruncated]      = useState(false);
  const [nameNotice,         setNameNotice]         = useState<NameNotice | null>(null);
  const [nameMode,           setNameMode]           = useState<StoreMappingModeInfo | null>(null);
  const [nameModeSaving,     setNameModeSaving]     = useState(false);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` });

  const fetchPlatformProducts = useCallback(async () => {
    if (!store?.id) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/mappings/${platform}`, { headers: authHeader() });
      if (r.ok) setPlatformProducts(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [store?.id, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInternalProducts = useCallback(async () => {
    if (!store?.id) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/internal-products`, { headers: authHeader() });
      if (r.ok) setInternalProducts(await r.json());
    } catch (e) { console.error(e); }
  }, [store?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNameMappings = useCallback(async () => {
    if (!store?.id) return;
    setNamesLoading(true);
    setNamesError(null);
    try {
      // ΡΗΤΟ όριο: χωρίς αυτό το backend κόβει στις 200 σιωπηλά. Στέλνουμε και τα
      // φίλτρα, ώστε το «φιλτράρισε για να δεις τις υπόλοιπες» να είναι αλήθεια.
      const params: StoreMappingListParams = { limit: NAME_MAPPINGS_MAX_LIMIT };
      if (nameStatusFilter !== 'all') params.status = nameStatusFilter;
      if (nameOptionsOnly) params.only_options = 1;
      if (nameQueryApplied) params.q = nameQueryApplied;
      const data: any = await storeNameMappingsAPI.list(store.id, platform, params);
      const rows: NameMappingRow[] = Array.isArray(data?.rows) ? data.rows : [];
      setNameRows(rows);
      setOrgNameRows(Array.isArray(data?.org_rows) ? data.org_rows : []);
      setNameCounts(data?.counts || {});
      setNameTruncated(rows.length >= NAME_MAPPINGS_MAX_LIMIT);
    } catch (e: any) {
      setNameRows([]);
      setOrgNameRows([]);
      setNameCounts({});
      setNameTruncated(false);
      setNamesError(apiErrorMessage(e) || t('products:nameLoadError'));
    } finally {
      setNamesLoading(false);
    }
  }, [store?.id, platform, nameStatusFilter, nameOptionsOnly, nameQueryApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Η ισχύουσα λειτουργία· σιωπηλή αποτυχία = απλώς δεν δείχνουμε τη γραμμή. */
  const fetchNameMode = useCallback(async () => {
    if (!store?.id) return;
    try {
      const data: any = await storeNameMappingsAPI.getMode(store.id);
      const mode = asMode(data && data.mode);
      setNameMode(mode
        ? { mode, store_override: asMode(data.store_override), org_mode: asMode(data.org_mode) }
        : null);
    } catch (e) {
      setNameMode(null);
    }
  }, [store?.id]);

  useEffect(() => {
    if (isOpen && store?.id) {
      fetchPlatformProducts();
      fetchInternalProducts();
      fetchNameMode();
    }
  }, [isOpen, store?.id, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ξεχωριστό effect για την ουρά ονομάτων: ξαναδιαβάζει και όταν αλλάξει φίλτρο
  // ή αναζήτηση, γιατί πλέον το φιλτράρισμα γίνεται στο backend.
  useEffect(() => {
    if (isOpen && store?.id) fetchNameMappings();
  }, [isOpen, store?.id, platform, nameStatusFilter, nameOptionsOnly, nameQueryApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  // Η αναζήτηση με μικρή καθυστέρηση: ένα ερώτημα ανά παύση, όχι ανά πλήκτρο.
  useEffect(() => {
    const handle = setTimeout(() => setNameQueryApplied(nameQuery.trim()), 350);
    return () => clearTimeout(handle);
  }, [nameQuery]);

  // Το modal μένει mounted μαζί με την οθόνη πλατφορμών, δηλαδή το πρώτο render
  // μπορεί να έγινε ΠΡΙΝ φορτώσει το `store`. Ξαναϋπολογίζουμε την προεπιλογή σε
  // κάθε άνοιγμα, αλλιώς ένα κεντρικό κατάστημα θα άνοιγε στους «Κωδικούς».
  useEffect(() => {
    if (isOpen) setActiveTab(orgManaged ? 'names' : 'codes');
  }, [isOpen, orgManaged]);

  // Auto-select first unmapped product on load
  useEffect(() => {
    if (platformProducts.length > 0 && selectedId === null) {
      const firstUnmapped = platformProducts.find(p => !p.internal_product_id);
      setSelectedId(firstUnmapped?.id ?? platformProducts[0].id);
    }
  }, [platformProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFetchCatalog = async () => {
    if (!store?.id) return;
    setSyncing(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/catalog/${platform}`, { headers: authHeader() });
      if (!r.ok) {
        const err = await r.json();
        showToast(`${t('common:error')}: ${err.details || err.error}`);
        return;
      }
      const data = await r.json();
      const imp = await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/import-catalog/${platform}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ products: data.products })
      });
      if (imp.ok) {
        const { newProducts, existingProducts } = await imp.json();
        showToast(t('products:newExisting', { newProducts, existingProducts }));
        await fetchPlatformProducts();
      }
    } catch (e) { showToast(t('products:catalogFetchError')); }
    finally { setSyncing(false); }
  };

  const handleMappingChange = async (product: PlatformProduct, internalProductId: number | null) => {
    if (!store?.id) return;
    try {
      const r = await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/mappings/${platform}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          platform_product_id:   product.platform_product_id,
          platform_product_name: product.platform_product_name,
          platform_product_code: product.platform_product_code,
          internal_product_id:   internalProductId,
          internal_size_id:      null
        })
      });
      if (r.ok) {
        const internalName = internalProducts.find(p => p.id === internalProductId)?.name;
        setPlatformProducts(prev => prev.map(p =>
          p.id === product.id
            ? { ...p, internal_product_id: internalProductId ?? undefined, internal_product_name: internalName }
            : p
        ));
      }
    } catch (e) { console.error(e); }
  };

  const handleClassificationChange = async (
    optionId: string, groupId: string, groupName: string, optionName: string,
    modifierType: string, internalSizeId?: number | null, internalProductId?: number | null
  ) => {
    if (!store?.id) return;
    try {
      await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/option-classifications/${platform}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          classifications: [{
            option_id: optionId,
            option_name: optionName,
            group_name: groupName,
            group_id: groupId,
            modifier_type: modifierType,
            is_manual_override: true,
            internal_size_id:    internalSizeId    ?? null,
            internal_product_id: internalProductId ?? null
          }]
        })
      });
    } catch (e) { console.error(e); }
  };

  // Server-side auto-map: links unmapped platform products to native products by
  // name, and fills size classifications product-scoped. Only fills NULLs — never
  // overwrites existing manual mappings. The user reviews/fixes the rest below.
  const handleAutoMatch = async () => {
    if (!store?.id) return;
    setAutoMatching(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/product-mappings/${store.id}/auto-map/${platform}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() }
      });
      if (!r.ok) {
        // Το 403 των κεντρικών καταστημάτων έχει δικό του ελληνικό μήνυμα.
        const err = await r.json().catch(() => ({} as any));
        showToast(err.error || t('products:autoMatchError'));
        return;
      }
      const res = await r.json();
      // Store fuzzy suggestions (one-click confirm in the product detail panel)
      const sugMap = new Map<string, { id: number; name: string; score: number }>();
      for (const s of (res.suggestions || [])) {
        sugMap.set(s.platform_product_id, { id: s.suggested_internal_id, name: s.suggested_internal_name, score: s.score });
      }
      setSuggestions(sugMap);
      setAutoMapResult({
        productsMatched:   Number(res.productsMatched)   || 0,
        sizesMatched:      Number(res.sizesMatched)      || 0,
        productsUnmatched: Number(res.productsUnmatched) || 0,
        productsAmbiguous: Number(res.productsAmbiguous) || 0,
        suggested:         sugMap.size,
        // Τα νούμερα των ΟΝΟΜΑΤΩΝ έρχονται με πρόθεμα name_* για να μην
        // μπερδεύονται με των κωδικών· κρατάμε fallback στα παλιά κλειδιά ώστε
        // η οθόνη να μη δείχνει μηδενικά αν ο server δεν έχει ενημερωθεί ακόμη.
        nameHarvested:     Number(res.name_harvested   ?? res.harvested)  || 0,
        nameSuggested:     Number(res.name_suggested   ?? res.suggested)  || 0,
        nameAutoMapped:    Number(res.name_auto_mapped ?? res.autoMapped) || 0,
      });
      await fetchPlatformProducts();
      // Το auto-map γράφει πλέον γραμμές ΟΝΟΜΑΤΩΝ (spec §8) — η καρτέλα «Ονόματα»
      // είναι η λίστα εργασίας που προκύπτει από αυτό, άρα γυρνάμε στα εκκρεμή:
      // αλλιώς ο πίνακας αποτελέσματος δείχνει ονόματα που δεν κατέβηκαν.
      setNameStatusFilter('pending');
      await fetchNameMappings();
    } catch (e) {
      showToast(t('products:autoMatchError'));
    } finally {
      setAutoMatching(false);
    }
  };

  // ── Καρτέλα «Ονόματα»: αποφάσεις ανθρώπου ──────────────────────────────────

  // Μετά από αλλαγή κατάστασης/στόχου ξαναδιαβάζουμε τη λίστα: αλλάζουν και τα
  // πλήθη των φίλτρων, και δεν θέλουμε δύο αντίγραφα της αλήθειας στην οθόνη.
  // ΕΞΑΙΡΕΣΗ τα συστατικά/το σπάσιμο: εκεί τίποτα από αυτά δεν αλλάζει, οπότε
  // κρατάμε τη γραμμή που απάντησε το PUT (ίδια μορφή με το GET, με τα ονόματα
  // των συστατικών μέσα). Χωρίς γραμμή στην απάντηση ξαναδιαβάζουμε κανονικά.
  const handleNameUpdate = async (row: NameMappingRow, body: NameUpdateBody) => {
    if (!store?.id) return;
    setNameBusyId(row.id);
    try {
      const saved: any = await storeNameMappingsAPI.update(store.id, platform, row.id, body);
      const fresh = isComponentOnlyBody(body) ? rowFromUpdate(saved, row.id) : null;
      if (fresh) {
        setNameRows(prev => prev.map(r => (r.id === row.id ? { ...r, ...fresh } : r)));
      } else {
        await fetchNameMappings();
      }
    } catch (e: any) {
      showToast(apiErrorMessage(e) || t('products:nameSaveError'));
    } finally {
      setNameBusyId(null);
    }
  };

  /**
   * 409 από suggest/accept-exact ΔΕΝ είναι «αποτυχία»: ή τρέχει ήδη πέρασμα για
   * το ίδιο κατάστημα, ή το κατάστημα δεν ανήκει σε αυτόν τον server. Και στις
   * δύο περιπτώσεις το backend στέλνει έτοιμο ελληνικό κείμενο και ο χρήστης
   * πρέπει να το ΔΙΑΒΑΣΕΙ — γι' αυτό μπαίνει σε μόνιμο μήνυμα, όχι σε toast.
   */
  const reportNameActionError = (e: any, fallbackKey: string) => {
    const text = apiErrorMessage(e);
    if (isMappingConflict(e)) {
      setNameNotice({ kind: 'warn', text: text || t(fallbackKey) });
      return;
    }
    setNameNotice({ kind: 'err', text: text || t(fallbackKey) });
    showToast(text || t(fallbackKey));
  };

  const handleNameSuggest = async () => {
    if (!store?.id) return;
    setNameSuggesting(true);
    setNameNotice(null);
    try {
      const res: any = await storeNameMappingsAPI.suggest(store.id, platform);
      showToast(t('products:nameSuggestDone', {
        harvested: res?.harvested ?? 0,
        suggested: res?.suggested ?? 0,
        auto:      res?.auto_mapped ?? 0,
      }));
      await fetchNameMappings();
    } catch (e: any) {
      reportNameActionError(e, 'products:nameSuggestError');
    } finally {
      setNameSuggesting(false);
    }
  };

  const handleNameAcceptExact = async () => {
    if (!store?.id) return;
    setNameAccepting(true);
    setNameNotice(null);
    try {
      const res: any = await storeNameMappingsAPI.acceptExact(store.id, platform);
      showToast(t('products:nameAcceptedCount', { count: res?.accepted ?? 0 }));
      await fetchNameMappings();
    } catch (e: any) {
      reportNameActionError(e, 'products:nameSaveError');
    } finally {
      setNameAccepting(false);
    }
  };

  /**
   * Αλλαγή λειτουργίας του καταστήματος. `null` ΣΒΗΝΕΙ το override, δηλαδή το
   * κατάστημα ξαναακολουθεί τον όμιλο — αυτό είναι που έλειπε και ένα rollback
   * του ομίλου σε 'off' άφηνε το κατάστημα να δουλεύει κανονικά.
   */
  const handleChangeNameMode = async (mode: MappingMode | null) => {
    if (!store?.id || nameModeSaving) return;
    setNameModeSaving(true);
    setNameNotice(null);
    try {
      const data: any = await storeNameMappingsAPI.setMode(store.id, mode);
      const applied = asMode(data && data.mode) || mode || 'off';
      setNameMode({
        mode: applied,
        store_override: data ? asMode(data.store_override) : mode,
        org_mode: data ? asMode(data.org_mode) : null,
      });
      showToast(t('products:nameModeSaved', { mode: t(NAME_MODE_LABEL_KEY[applied]) }));
    } catch (e: any) {
      setNameNotice({ kind: 'err', text: apiErrorMessage(e) || t('products:nameModeSaveError') });
    } finally {
      setNameModeSaving(false);
    }
  };

  // Filtered list — ταξινομημένη κατά πλήθος παραγγελιών: ό,τι πουλάει πιο πολύ
  // κοστίζει και πιο πολύ όταν μένει χωρίς αντιστοίχιση.
  const filtered = platformProducts.filter(p => {
    if (filter === 'mapped'   && !p.internal_product_id) return false;
    if (filter === 'unmapped' &&  p.internal_product_id) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return p.platform_product_name.toLowerCase().includes(q) ||
             p.platform_product_code?.toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => (Number(b.order_count) || 0) - (Number(a.order_count) || 0));

  const selectedProduct = platformProducts.find(p => p.id === selectedId) ?? null;
  const mappedCount   = platformProducts.filter(p =>  p.internal_product_id).length;
  const unmappedCount = platformProducts.filter(p => !p.internal_product_id).length;

  // Ονόματα που περιμένουν άνθρωπο — τα πιο συχνά πρώτα (πίνακας auto-match).
  const pendingNameRows = nameRows
    .filter(r => r.status === 'pending')
    .sort((a, b) => (b.seen_count || 0) - (a.seen_count || 0));
  const pendingNameCount = nameCounts.pending ?? pendingNameRows.length;

  // ── Καρτέλες: ΟΝΟΜΑΤΑ (σταθερό κλειδί) vs ΚΩΔΙΚΟΙ (αλλάζουν σε κάθε μενού) ──
  const tabs: Array<SubTab<'names' | 'codes'>> = [
    {
      id: 'names',
      label: t('products:tabNames'),
      badge: pendingNameCount > 0 ? <PendingBadge count={pendingNameCount} /> : null,
      testId: 'mapping-tab-names',
    },
    {
      id: 'codes',
      label: t('products:tabPlatformCodes'),
      badge: unmappedCount > 0 ? <PendingBadge count={unmappedCount} /> : null,
      testId: 'mapping-tab-codes',
    },
  ];

  const codesActive = activeTab === 'codes';

  return (
    <DetailPanel
      open={isOpen}
      onClose={onClose}
      width="lg"
      title={`${t('products:productMapping')} ${PLATFORM_NAMES[platform]}`}
      subtitle={`${mappedCount} ${t('products:mapped')} · ${unmappedCount} ${t('products:pending')} · ${platformProducts.length} ${t('products:totalCount')}`}
      footer={(
        <>
          {(unmappedCount > 0 || activeTab === 'names') && canWriteNames && (
            <button
              type="button"
              onClick={handleAutoMatch}
              disabled={autoMatching}
              className={GHOST_BTN}
              title={t('products:autoMatchLabel')}
            >
              {autoMatching
                ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <Link2 className="h-4 w-4" aria-hidden="true" />}
              {autoMatching ? t('products:applying') : t('products:autoMatchByName')}
            </button>
          )}
          <button
            type="button"
            onClick={handleFetchCatalog}
            disabled={syncing}
            className={PRIMARY_BTN}
          >
            {syncing
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <Download className="h-4 w-4" aria-hidden="true" />}
            {syncing ? t('products:fetcingCatalog') : t('products:fetchCatalog')}
          </button>
        </>
      )}
      testId="product-mapping"
    >
      {/* Το περιεχόμενο γεμίζει το συρτάρι (≥640px): οι λίστες κυλούν μόνες τους και οι καρτέλες,
          τα μηνύματα και οι εργαλειοθήκες μένουν στη θέση τους. Στο κινητό κυλά όλο μαζί. */}
      <div className="flex flex-col gap-4 sm:h-full sm:min-h-0">
        <div className="shrink-0">
          <SubTabs
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            label={t('products:productMapping')}
            testId="mapping-tabs"
          />
        </div>

        {/* Toast */}
        {toast && (
          <Banner tone="info" role="status" className="shrink-0" testId="mapping-toast">{toast}</Banner>
        )}

        {/* ── Πίνακας αποτελέσματος auto-match (μένει μέχρι να τον κλείσει ο χρήστης) ── */}
        {autoMapResult && (
          <Banner
            tone="info"
            icon={Link2}
            className="shrink-0"
            onDismiss={() => setAutoMapResult(null)}
            dismissLabel={t('common:close')}
            testId="auto-map-result"
          >
            <p className="font-semibold text-t1">{t('products:autoMapPanelTitle')}</p>
            {/* Δύο γραμμές, μία ανά πίνακα: το auto-map γράφει ΚΑΙ κωδικούς
                πλατφόρμας ΚΑΙ ονόματα, και τα νούμερα δεν αθροίζονται. */}
            <p className="mt-1 text-xs text-t3">
              <span className="font-semibold text-t2">{t('products:tabPlatformCodes')}:</span>{' '}
              {[
                t('products:autoMapPanelMatched', { count: autoMapResult.productsMatched }),
                t('products:autoMapPanelSizes', { count: autoMapResult.sizesMatched }),
                t('products:autoMapPanelPending', { count: autoMapResult.productsUnmatched }),
                t('products:autoMapPanelAmbiguous', { count: autoMapResult.productsAmbiguous }),
                autoMapResult.suggested > 0 ? t('products:autoMapPanelSuggested', { count: autoMapResult.suggested }) : '',
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="mt-0.5 text-xs text-t3">
              <span className="font-semibold text-t2">{t('products:tabNames')}:</span>{' '}
              {[
                t('products:autoMapPanelHarvested', {
                  count: autoMapResult.nameHarvested,
                  defaultValue: '{{count}} νέα ονόματα'
                }),
                t('products:autoMapPanelSuggested', { count: autoMapResult.nameSuggested }),
                t('products:autoMapPanelAutoMapped', {
                  count: autoMapResult.nameAutoMapped,
                  defaultValue: '{{count}} αυτόματα'
                }),
              ].join(' · ')}
            </p>

            {pendingNameRows.length > 0 && (
              <div className="mt-2.5">
                <p className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-t3">{t('products:autoMapPendingNames')}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {pendingNameRows.slice(0, 12).map(row => (
                    <button
                      key={`pending-${row.id}`}
                      type="button"
                      onClick={() => setActiveTab('names')}
                      title={t('products:autoMapGoToNames')}
                      className={chipClass(false)}
                    >
                      <span className="max-w-[16rem] truncate">{row.display_name}</span>
                      {(row.seen_count || 0) > 0 && (
                        <>{' '}<span className="text-xs tabular-nums text-t4">×{row.seen_count}</span></>
                      )}
                      {row.vat_risk && (
                        <>{' '}<StatusChip tone="danger">{t('products:nameVatRiskBadge')}</StatusChip></>
                      )}
                    </button>
                  ))}
                  {pendingNameRows.length > 12 && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('names')}
                      className={`${LINK_BTN} px-1`}
                    >{t('products:autoMapMoreNames', { count: pendingNameRows.length - 12 })}</button>
                  )}
                </div>
              </div>
            )}
          </Banner>
        )}

        {/* ── Καρτέλα «Ονόματα» ── */}
        {activeTab === 'names' && (
          <NameMappingsTab
            rows={nameRows}
            orgRows={orgNameRows}
            counts={nameCounts}
            loading={namesLoading}
            error={namesError}
            notice={nameNotice}
            onDismissNotice={() => setNameNotice(null)}
            statusFilter={nameStatusFilter}
            onStatusFilter={setNameStatusFilter}
            query={nameQuery}
            onQuery={setNameQuery}
            optionsOnly={nameOptionsOnly}
            onOptionsOnly={setNameOptionsOnly}
            truncated={nameTruncated}
            modeInfo={nameMode}
            canManageMode={canManageMode}
            modeSaving={nameModeSaving}
            onChangeMode={handleChangeNameMode}
            orgManaged={orgManaged}
            canWrite={canWriteNames}
            busyId={nameBusyId}
            suggesting={nameSuggesting}
            accepting={nameAccepting}
            internalProducts={internalProducts}
            onUpdate={handleNameUpdate}
            onSuggest={handleNameSuggest}
            onAcceptExact={handleNameAcceptExact}
          />
        )}

        {/* ── Καρτέλα «Κωδικοί πλατφόρμας»: λίστα αριστερά, λεπτομέρεια δεξιά (στο κινητό η μία κάτω
            από την άλλη). Μένει mounted όπως πάντα· κρυμμένη όταν είναι ανοιχτά τα «Ονόματα». ── */}
        <div
          role="tabpanel"
          aria-label={t('products:tabPlatformCodes')}
          hidden={!codesActive}
          className={codesActive ? 'flex flex-col gap-3 sm:min-h-0 sm:flex-1' : undefined}
          data-testid="mapping-codes-tab"
        >
          {/* Αναζήτηση + φίλτρο */}
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <SearchField
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('common:searchPlaceholder')}
              label={t('common:search')}
              className="min-w-[200px] flex-1 sm:max-w-xs"
            />
            <div role="group" aria-label={t('common:status')} className={SEG_WRAP}>
              {(['all', 'mapped', 'unmapped'] as const).map(f => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className={segBtn(filter === f)}
                >
                  {f === 'all'
                    ? t('products:allFilter')
                    : f === 'mapped' ? t('products:mappingPanel.filterMapped') : t('products:mappingPanel.filterPending')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col border-t border-line sm:min-h-0 sm:flex-1 sm:flex-row">
            {/* ── Λίστα προϊόντων πλατφόρμας ── */}
            <ul
              aria-label={t('products:tabPlatformCodes')}
              aria-busy={loading || undefined}
              className="-mx-3 max-h-[45vh] flex-none overflow-y-auto border-b border-line sm:mx-0 sm:-ml-3 sm:max-h-none sm:w-64 sm:border-b-0 sm:border-r"
              data-testid="mapping-code-list"
            >
              {loading ? (
                [0, 1, 2, 3, 4].map(i => (
                  <li key={i} aria-hidden="true" className="border-t border-line px-3 py-3.5 first:border-t-0">{skeletonLines(i)}</li>
                ))
              ) : filtered.length === 0 ? (
                <li className="px-3 py-10 text-center text-sm text-t3">
                  {platformProducts.length === 0
                    ? t('products:clickFetchCatalog')
                    : t('products:noProductsFound')}
                </li>
              ) : (
                filtered.map(product => {
                  const selected = selectedId === product.id;
                  const optionGroupCount = product.platform_metadata?.option_groups?.length || 0;
                  const lastSeen = formatShortDate(product.last_seen_at);
                  // Από πού ήρθε η γραμμή και πότε την είδαμε τελευταία: ο κωδικός
                  // της πλατφόρμας αλλάζει σε κάθε μενού, η ημερομηνία δείχνει
                  // ποιες γραμμές έχουν ήδη «σβήσει».
                  const meta = [
                    product.match_source && CODE_SOURCE_LABEL_KEY[product.match_source] ? t(CODE_SOURCE_LABEL_KEY[product.match_source]) : '',
                    (product.order_count || 0) > 0 ? `×${product.order_count}` : '',
                    lastSeen ? t('products:codeLastSeen', { date: lastSeen }) : '',
                    optionGroupCount > 0 ? t('products:codeOptionGroups', { count: optionGroupCount }) : '',
                  ].filter(Boolean);
                  return (
                    <li key={product.id} className="border-t border-line first:border-t-0">
                      <button
                        type="button"
                        onClick={() => setSelectedId(product.id)}
                        aria-current={selected ? 'true' : undefined}
                        className={`relative block w-full px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                          selected ? 'bg-brand-soft' : 'hover:bg-surface-2'
                        }`}
                      >
                        {/* Η επιλεγμένη γραμμή όπως στον «Πίνακα»: απαλό χρώμα + λωρίδα αριστερά. */}
                        {selected && <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
                        <span className="flex items-baseline gap-2">
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-t1">{product.platform_product_name}</span>
                          {' '}
                          {product.platform_product_price != null && (
                            <span className="shrink-0 text-xs tabular-nums text-t3">{formatMoney(Number(product.platform_product_price))}</span>
                          )}
                        </span>
                        {product.internal_product_name ? (
                          <span className="mt-0.5 block truncate text-xs text-t2">→ {product.internal_product_name}</span>
                        ) : !product.internal_product_id ? (
                          <span className="mt-1 flex"><StatusChip tone="warn">{t('products:nameStatusPending')}</StatusChip></span>
                        ) : null}
                        {meta.length > 0 && (
                          <span className="mt-0.5 block truncate text-xs text-t4">{meta.join(' · ')}</span>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>

            {/* ── Λεπτομέρεια του επιλεγμένου ── */}
            <div className="min-w-0 flex-1 pt-4 sm:min-h-0 sm:overflow-y-auto sm:pl-5">
              {selectedProduct ? (
                <ProductDetail
                  product={selectedProduct}
                  internalProducts={internalProducts}
                  platform={platform}
                  suggestion={suggestions.get(selectedProduct.platform_product_id)}
                  onMappingChange={handleMappingChange}
                  onClassificationChange={handleClassificationChange}
                />
              ) : (
                <EmptyState title={t('products:selectProductFromList')} testId="mapping-no-selection" />
              )}
            </div>
          </div>
        </div>
      </div>
    </DetailPanel>
  );
};

export default ProductMappingModal;
